import LinkIcon from "@mui/icons-material/Link";
import LockResetIcon from "@mui/icons-material/LockReset";
import PhoneIcon from "@mui/icons-material/Phone";
import { Button, Grid2 as Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TrackedTextField } from "src/theme/StyledFields";
import OneTimeLoginLink from "../../components/OneTimeLoginLink";
import { storeReturnParams } from "../../utils/onboarding";
import { captureSsoFromSearch } from "../../utils/sso";
import AlternateSignInOptions from "../components/AlternateSignInOptions";
import AuthIntentTabs, { authHref } from "../components/AuthIntentTabs";
import FormErrors, { hasError } from "../components/FormErrors";
import PasswordField from "../components/PasswordField";
import { login } from "../lib/allauth";

export default function Login(props) {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [response, setResponse] = useState({ fetching: false, content: null });
  const autoOpenMagicLink = searchParams.get("magic-link") === "open";
  const searchSuffix = searchParams.toString()
    ? `?${searchParams.toString()}`
    : "";

  // Capture SSO mode and any redirect/next params at login so a login-only
  // SSO entry (bounced here from /auth/sso-session/ with ?sso=true) still
  // re-runs provisioning and honors its onboarding destination after auth.
  useEffect(() => {
    captureSsoFromSearch(window.location.search);
    storeReturnParams(window.location.search);
  }, []);

  function submit() {
    setResponse({ ...response, fetching: true });
    const payload = { password };
    if (email.indexOf("@") > -1) {
      payload.email = email;
    } else {
      payload.username = email;
    }
    login(payload)
      .then((content) => {
        setResponse((r) => {
          // Normalize responses that only contain a status code or have no content
          let normalized = content;
          try {
            const isEmpty =
              content == null ||
              (typeof content === "object" && Object.keys(content).length === 0);

            const isStatusOnly =
              typeof content === "object" &&
              content !== null &&
              "status" in content &&
              !("errors" in content) &&
              !("detail" in content) &&
              !("message" in content) &&
              !("text" in content);

            if (isEmpty) {
              normalized = {
                errors: [
                  {
                    code: "empty_response",
                    message:
                      "Unexpected empty response from server. Please try again.",
                  },
                ],
              };
            } else if (isStatusOnly) {
              const status = content.status;
              let code = "server_error";
              let message = `Request failed with status ${status}. Please try again.`;
              if (status === 409) {
                code = "invalid_credentials";
                message = "Invalid email or password.";
              } else if (status === 401) {
                code = "unauthorized";
                message = "Unauthorized. Please check your credentials.";
              } else if (status >= 500) {
                code = "server_error";
                message = "Server error. Please try again later.";
              }
              normalized = { errors: [{ code, message }] };
            }
          } catch (e) {
            // If normalization fails, surface a generic error
            normalized = {
              errors: [
                {
                  code: "parse_error",
                  message: "Unable to process server response. Please try again.",
                },
              ],
            };
          }
          return { ...r, content: normalized };
        });
      })
      .catch((e) => {
        setResponse((r) => {
          return { ...r, content: { errors: [{ code: 'invalid_credentials', message: e.toString() }] } };
        });
        console.error(e);
        window.alert(e);
      })
      .then(() => {
        setResponse((r) => {
          return { ...r, fetching: false };
        });
      });
  }

  return (
    <Grid container direction="column" p={1} gap={1} mt={8} style={{ margin: "auto", maxWidth: "700px" }} data-testid="login-page">
      <AuthIntentTabs
        intent="login"
        method="email"
        onIntentChange={
          props?.onShowSignup
            ? (next) => {
              if (next === "signup") {
                props.onShowSignup();
              }
            }
            : undefined
        }
      />

      <Typography variant="h5">Email & Password</Typography>

      <Grid item marginBottom={1}>
        <TrackedTextField
          label="Email"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && email.indexOf("@") >= 1 && password.length > 0 && !response.fetching) {
              submit();
            }
          }}
          type="email"
          required
          error={hasError(response.content?.errors, "email")}
          data-attr="login_field-email"
        />
        <FormErrors param="email" errors={response.content?.errors} />
      </Grid>
      <Grid item marginBottom={1}>
        <PasswordField
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && email.indexOf("@") >= 1 && password.length > 0 && !response.fetching) {
              submit();
            }
          }}
          required
          label="Password"
          error={hasError(response.content?.errors, "password")}
          data-attr="login_field-password"
        />
        <FormErrors param="password" errors={response.content?.errors} />
      </Grid>
      <Grid item>
        <Button
          onClick={submit}
          disabled={
            email.indexOf("@") < 1 ||
            password.length === 0 ||
            response.fetching
          }
          variant="contained"
          size="small"
          data-attr="login_submit"
        >
          Login
        </Button>
      </Grid>
      <Grid item style={{ textAlign: "center" }}>
        <FormErrors errors={response.content?.errors} />
      </Grid>

      <AlternateSignInOptions
        methodToggle={
          <Button
            component={Link}
            to={authHref("login", "sms", searchSuffix)}
            variant="outlined"
            color="primary"
            fullWidth
            startIcon={<PhoneIcon />}
            onClick={() => props?.onClose?.()}
            data-attr="login_sms"
          >
            Use SMS
          </Button>
        }
      >
        <OneTimeLoginLink
          email={email}
          defaultOpen={autoOpenMagicLink}
          layout="modal"
          fullWidth
          variant="contained"
          color="secondary"
          startIcon={<LinkIcon />}
          dataAttr="alternate-signin_magic-link"
        />
        <Button
          component={Link}
          to="/account/password/reset"
          variant="contained"
          color="secondary"
          fullWidth
          startIcon={<LockResetIcon />}
          onClick={() => props?.onClose?.()}
          data-attr="login_forgot-password"
        >
          Forgot my password
        </Button>
      </AlternateSignInOptions>

    </Grid>
  );
}
